/**
 * CEPI chat "I need help with this query" support report.
 *
 * The trigger is a deterministic WHOLE-MESSAGE phrase match, checked in the
 * panel before anything is sent to CEPI. A message that merely contains one
 * of these phrases ("contact support for Keytruda dosing") is an ordinary
 * question and goes to CEPI unchanged, so no label search can be hijacked.
 */
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from './impersonationHeaders';

const API_BASE_URL = import.meta.env.VITE_API_URL ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

const TRIGGER_PHRASES = new Set([
  'i need help with this query',
  'i need help with this',
  'i need help',
  'help with this query',
  'report a problem',
  'report an issue',
  'report a bug',
  'contact support',
]);

export function isSupportRequest(text: string): boolean {
  const normalized = text
    .toLowerCase()
    .trim()
    .replace(/[.!?]+$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^please /, '')
    .replace(/ please$/, '');
  return TRIGGER_PHRASES.has(normalized);
}

export interface SupportReportBody {
  query: string;
  search_query: string;
  page: string;
  notes: string;
  conversation_id: string | null;
}

/** Resolves only when the server confirms the email was sent; throws otherwise. */
export async function submitSupportReport(body: SupportReportBody): Promise<void> {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken(false) : null;
  const res = await fetch(`${API_BASE_URL}/chat/support-report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...getImpersonationHeaders(),
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || data?.sent !== true) {
    throw new Error(typeof data?.detail === 'string' ? data.detail : 'Your report could not be sent.');
  }
}
